import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../api';
import Layout from '../components/Layout';
import ROLE_HOME from '../roleHome';

const STATUS_LABEL = { queue: 'В очереди', production: 'В производстве' };
const STATUS_COLOR = { queue: '#f59e0b', production: 'var(--accent)' };

// Katak rangi — qolgan miqdorga qarab
function cellColor(remaining) {
  if (!remaining) return null;
  if (remaining >= 200) return { bg: '#ef444418', color: '#ef4444', border: '#ef444430' };
  if (remaining >= 100) return { bg: '#f59e0b14', color: '#f59e0b', border: '#f59e0b28' };
  return { bg: '#22c55e12', color: '#22c55e', border: '#22c55e25' };
}

// Со скольки минут цифру пора считать несвежей и подсвечивать время.
// У пресса датчик опрашивают на каждом прессовании, поэтому пять минут —
// это уже разрыв. Воздух в цехе меняется медленно и висит на отдельном
// питании через wi-fi: полчаса там ничего не значат.
const STALE_MIN = 5;
const SHOP_STALE_MIN = 30;

export default function ProductionBoard() {
  // Как раскладывать очередь: по матрицам (как было) или по прессам
  const [mode, setMode] = useState('texture');
  const navigate  = useNavigate();
  const role = sessionStorage.getItem('role');
  const isOperator = role === 'operator';
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');
  const [expanded, setExpanded] = useState({});
  // Карточки прессов занимают верх экрана, а очередь — то, ради чего щит и
  // открывают: за ней приходилось листать. Свернули один раз — щит помнит.
  const [pressOpen, setPressOpen] = useState(() => {
    try { return localStorage.getItem('board_presses') !== 'off'; }
    catch (e) { return true; }
  });
  const togglePresses = () => setPressOpen(v => {
    try { localStorage.setItem('board_presses', v ? 'off' : 'on'); } catch (e) {}
    return !v;
  });

  const load = useCallback(async () => {
    try {
      const r = await analyticsAPI.getProductionBoard();
      setData(r.data);
      setLastUpdate(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const toggleRow = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const goToMyPage = () => {
    const home = ROLE_HOME[role] || '/';
    navigate(home);
  };

  if (loading) return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <p style={{ color: 'var(--muted)', fontSize: 18 }}>{t('Загрузка производственного щита...')}</p>
    </div>
  );

  // Матрица отвечает на вопрос «что можно сделать без переналадки», пресс —
  // «кто это делает». Вопросы разные, и оба задают: технолог смотрит по
  // матрицам, а к прессу подходят и спрашивают, что стоит на нём.
  const byPress      = mode === 'press';
  const cols         = byPress
    ? (data?.queue_presses || []).map(p => ({ id: p.id, name: p.name }))
    : (data?.textures || []).map(x => ({ id: x.id, name: x.name }));
  const cellOf       = (row, colId) => (byPress ? row.by_press : row.by_texture)
    ?.[String(colId)];
  const rows         = data?.rows     || [];
  const presses      = data?.active_presses || [];
  const totalRemain  = data?.total_remaining || 0;

  // Заказ, который лежит на прессе прямо сейчас. В матрице сотня строк, и
  // найти среди них ту, что печатается, приходилось глазами по номеру
  // заказа из карточки наверху. Сопоставляем по номеру заказа внутри
  // колонки пресса — совпадение точное, тёзок у номера не бывает.
  const nowByPress = {};
  presses.forEach(p => {
    const st = p.state || (p.texture_name ? 'working' : 'idle');
    if ((st === 'working' || st === 'waiting') && p.order_number) {
      nowByPress[String(p.press_id)] = {
        order: p.order_number, working: st === 'working',
      };
    }
  });
  const cellIsNow = (row, colId) => {
    if (!byPress) return null;
    const nw = nowByPress[String(colId)];
    if (!nw) return null;
    const hit = (cellOf(row, colId)?.orders || [])
      .some(o => o.order_number === nw.order);
    return hit ? nw : null;
  };

  return (
    <Layout title={t("Что делается сейчас")}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {totalRemain > 0 && (
          <span style={{ background: '#ef444420', color: '#ef4444', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
            {totalRemain.toLocaleString()} листов к выполнению
          </span>
        )}
        {/* Воздух и влажность — ОДИН датчик на весь цех. Своей полосой во
            всю ширину они съедали строку экрана ради двух чисел, а до
            очереди приходилось листать. Верхняя строка и так наполовину
            пустая — им там место. В карточки прессов эти числа класть
            нельзя: повторить их у каждого значило бы сказать, что у
            прессов разный воздух. */}
        {data?.shop && (
          <span style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 8,
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 20, padding: '4px 14px', whiteSpace: 'nowrap',
          }}>
            <span style={{ color: 'var(--muted)', fontSize: 12.5, fontWeight: 700 }}>
              🏭 {t('В цехе')}
            </span>
            <b style={{ color: 'var(--text)', fontSize: 15 }}>{data.shop.temp}°</b>
            {data.shop.humidity != null && (
              <>
                <span style={{ color: 'var(--border)' }}>·</span>
                <b style={{ color: 'var(--text)', fontSize: 15 }}>{data.shop.humidity}%</b>
              </>
            )}
            <span style={{
              fontSize: 11.5,
              color: Math.max(data.shop.temp_late || 0, data.shop.humidity_late || 0) > SHOP_STALE_MIN
                ? '#f59e0b' : 'var(--dim)',
            }}>{data.shop.temp_at}</span>
          </span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpdate && <span style={{ color: 'var(--dim)', fontSize: 12 }}>Обновлено: {lastUpdate}</span>}
          <button onClick={load} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}>↻</button>
          {/* Оператор попадает на щит первым делом, и эта кнопка — единственное,
              что ему тут нужно нажать. Обычной кнопкой в углу она терялась среди
              таблиц: у пресса смотрят на экран мельком и не глазами офисного
              пользователя. Остальным ролям щит — справка, им кнопка помельче. */}
          {/* Кнопка нужна только оператору: он попадает на щит первым делом,
              и уйти отсюда к своему прессу — единственное, что ему тут надо.
              Остальным она вела «к себе домой», куда и так ведёт левое меню, а
              у контролёра качества и вовсе выбрасывала на страницу входа:
              адрес в списке был написан неверно. */}
          {isOperator && <button
            onClick={goToMyPage}
            className="touch-btn cta-pulse"
            style={{
              background: 'var(--accent)', border: 'none', borderRadius: 14, color: '#fff',
              minHeight: 66, padding: '0 32px', cursor: 'pointer',
              fontSize: 22, fontWeight: 800, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: '0 10px 24px rgba(0,0,0,.28)',
            }}
          >
            <><span style={{ fontSize: 26 }}>🖥️</span> Начать работу</>
          </button>}
        </div>
      </div>

      {/* Колонка во всю оставшуюся высоту. Раньше у таблицы стоял потолок
          «100vh минус 280», и он же был её единственным размером: при пяти
          строках под ней оставалось пустое поле, а на уменьшенном масштабе —
          в половину экрана. Теперь прессы занимают своё, а очередь — всё
          остальное, сколько бы его ни было.
          58 — шапка страницы, 24+28 — поля области, ~56 — строка сверху. */}
      <div style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        height: 'calc(100vh - 166px)',
      }}>

        {/* ── Активные прессы ── */}
        <div style={{ marginBottom: 20, flexShrink: 0 }}>
          <h2 onClick={togglePresses} style={{
            color: 'var(--muted)', fontSize: 12, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            userSelect: 'none',
          }} title={pressOpen ? 'Свернуть' : 'Развернуть'}>
            <span style={{ fontSize: 10 }}>{pressOpen ? '▼' : '▶'}</span>
            ⚡ Прессы — текущая работа
          </h2>

          {/* Свёрнуто — но не спрятано: строкой остаётся то, за чем на щит и
              смотрят мельком, а показания станка разворачиваются по клику. */}
          {!pressOpen && (
            <div onClick={togglePresses} style={{
              display: 'flex', gap: 10, flexWrap: 'wrap', cursor: 'pointer',
            }}>
              {presses.map(p => {
                const st = p.state || (p.texture_name ? 'working' : 'idle');
                const ac = st === 'working' ? '#22c55e'
                  : st === 'waiting' ? '#f59e0b' : 'var(--dim)';
                return (
                  <span key={p.press_id} style={{
                    display: 'inline-flex', alignItems: 'baseline', gap: 8,
                    background: 'var(--card)', border: '1px solid ' + ac + '55',
                    borderRadius: 20, padding: '5px 14px', whiteSpace: 'nowrap',
                  }}>
                    <b style={{ fontSize: 13 }}>{p.press_name}</b>
                    <span style={{ color: ac, fontSize: 12, fontWeight: 700 }}>
                      {st === 'working' ? 'Работает' : st === 'waiting' ? 'Загружен' : 'Простой'}
                    </span>
                    {p.quantity_planned > 0 && (
                      <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                        {p.quantity_done} / {p.quantity_planned}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}

          <div style={{ display: pressOpen ? 'flex' : 'none', gap: 12, flexWrap: 'wrap' }}>
            {presses.map(p => {
              const state = p.state || (p.texture_name ? 'working' : 'idle');
              const isWorking = state === 'working';
              const isWaiting = state === 'waiting';
              const accent = isWorking ? '#22c55e' : isWaiting ? '#f59e0b' : null;
              const pct = p.quantity_planned > 0
                ? Math.min(100, Math.round(p.quantity_done / p.quantity_planned * 100)) : 0;
              return (
                <div key={p.press_id} style={{
                  background: 'var(--card)',
                  border: '1px solid ' + (accent ? accent + '55' : 'var(--border)'),
                  borderRadius: 12, padding: '14px 18px',
                  minWidth: 280, flex: '1 1 280px', maxWidth: 380,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>⚙️ {p.press_name}</span>
                    <span style={{
                      background: accent ? accent + '20' : 'var(--bg)',
                      color: accent || 'var(--dim)',
                      borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 700,
                    }}>
                      {isWorking ? '🟢 Работает' : isWaiting ? '⏳ Загружен' : '⏸ Простой'}
                    </span>
                  </div>
                  {(isWorking || isWaiting) ? (
                    <>
                      <div style={{ marginBottom: 6 }}>
                        <span style={{
                          background: 'var(--accent-weak)', color: 'var(--accent)',
                          borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 600,
                          marginRight: 8,
                        }}>{p.texture_name}</span>
                        <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>
                          {p.product_name || '—'}
                        </span>
                      </div>
                      {p.order_number && (
                        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 8 }}>
                          {p.order_number} · {p.client_name}
                          {p.operator_name && ` · ${p.operator_name}`}
                        </div>
                      )}
                      {isWaiting && (
                        <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
                          ⏳ Ожидает запуска оператором
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 6, height: 8 }}>
                          <div style={{
                            width: `${isWaiting ? 0 : pct}%`, height: 8, background: accent || '#22c55e',
                            borderRadius: 6, transition: 'width 0.4s',
                          }} />
                        </div>
                        <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700, minWidth: 80, textAlign: 'right' }}>
                          {p.quantity_done} / {p.quantity_planned}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Нет активного задания')}</div>
                  )}

                  {/* Что станок говорит прямо сейчас — те же величины, что
                      цепляются к листу. Не для разбора: взглянул и понял,
                      что пресс в режиме. Щит обновляет себя сам, так что
                      «последнее» тут и значит «сейчас». */}
                  {p.reading && (
                    <div style={{
                      display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10,
                      paddingTop: 10, borderTop: '1px solid var(--border)',
                    }}>
                      {[
                        ['Верх',    p.reading.temp_top,     '°'],
                        ['Низ',     p.reading.temp_bottom,  '°'],
                        ['Выдержка', p.reading.hold_seconds, ' с'],
                        ['Давление', p.reading.pressure,     ''],
                      ].map(([label, v, unit]) => v && (
                        <div key={label}>
                          <div style={{ color: 'var(--dim)', fontSize: 11 }}>{t(label)}</div>
                          <div style={{
                            color: v.late > STALE_MIN ? 'var(--dim)' : 'var(--text)',
                            fontSize: 17, fontWeight: 800, lineHeight: 1.2,
                          }}>
                            {v.value}{unit}
                          </div>
                          {/* Во сколько датчик это снял. Не «12 минут назад»:
                              такую цифру надо вычитать из часов в уме, а
                              14:32 сверять не надо. Датчик нижней плиты на
                              третьем прессе отваливается на пару минут и всё
                              это время повторяет последнее значение — здесь
                              сразу видно, что цифра оттуда. */}
                          <div style={{
                            fontSize: 11, marginTop: 1,
                            color: v.late > STALE_MIN ? '#f59e0b' : 'var(--dim)',
                            fontWeight: v.late > STALE_MIN ? 700 : 400,
                          }}>{v.at}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Производственный стол ── */}
        {rows.length === 0 ? (
          <div style={{
            background: 'var(--card)', borderRadius: 16, padding: '48px 20px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <p style={{ color: 'var(--muted)', fontSize: 18 }}>
              Нет активных заказов к выполнению
            </p>
            <p style={{ color: 'var(--dim)', fontSize: 14, marginTop: 8 }}>
              Все заказы выполнены или ожидают согласования
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column',
                        flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                          flexWrap: 'wrap', marginBottom: 10, flexShrink: 0 }}>
              <h2 style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 1, margin: 0, flex: 1 }}>
                📋 Очередь производства — листов осталось выполнить
              </h2>
              {/* Один и тот же список, но разложенный под разные вопросы */}
              <div style={{ display: 'flex', gap: 6 }}>
                {[{ k: 'texture', l: 'По матрицам' }, { k: 'press', l: 'По прессам' }].map(x => (
                  <button key={x.k} onClick={() => setMode(x.k)} className="touch-btn" style={{
                    padding: '7px 15px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit',
                    background: mode === x.k ? 'var(--accent)' : 'var(--bg)',
                    border: '1px solid ' + (mode === x.k ? 'var(--accent)' : 'var(--border)'),
                    color: mode === x.k ? '#fff' : 'var(--muted)',
                  }}>{t(x.l)}</button>
                ))}
              </div>
            </div>
            <div style={{
              overflowX: 'auto', overflowY: 'auto',
              flex: 1, minHeight: 220,
              borderRadius: 12, border: '1px solid var(--border)',
              background: 'var(--card)',
            }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse',
                background: 'var(--card)', minWidth: 600,
              }}>
                {/* Заголовок таблицы */}
                <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                  <tr>
                    <th style={{
                      padding: '12px 16px', textAlign: 'left',
                      color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                      borderBottom: '2px solid var(--border)', minWidth: 260,
                      position: 'sticky', left: 0, top: 0,
                      background: 'var(--card)', zIndex: 30,
                    }}>
                      Продукт / Заказ
                    </th>
                    {cols.map(t => (
                      <th key={t.id} style={{
                        padding: '12px 16px', textAlign: 'center',
                        color: 'var(--accent)', fontSize: 13, fontWeight: 700,
                        borderBottom: '2px solid var(--border)', minWidth: 100,
                        borderLeft: '1px solid var(--border)',
                        background: 'var(--card)',
                      }}>
                        {t.name}
                      </th>
                    ))}
                    <th style={{
                      padding: '12px 16px', textAlign: 'center',
                      color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                      borderBottom: '2px solid var(--border)', borderLeft: '2px solid var(--border)',
                      minWidth: 80, background: 'var(--card)',
                    }}>
                      Итого
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, rowIdx) => {
                    const isExpanded = expanded[row.nomenclature_id];
                    // Строка, которая печатается прямо сейчас, подсвечена
                    // целиком: цифра в одной ячейке теряется среди сотни.
                    const rowNow = cols.some(c => cellIsNow(row, c.id));
                    const rowBg = rowNow ? 'var(--accent-weak)'
                      : rowIdx % 2 === 0 ? 'var(--card)' : 'var(--bg)';

                    return (
                      <React.Fragment key={row.nomenclature_id}>
                        {/* Строка продукта (сводная) */}
                        <tr
                          onClick={() => toggleRow(row.nomenclature_id)}
                          style={{
                            cursor: 'pointer', background: rowBg,
                            borderBottom: isExpanded ? 'none' : '1px solid var(--border)',
                          }}
                          title={t("Нажмите чтобы раскрыть заказы")}
                        >
                          <td style={{
                            padding: '12px 16px', fontWeight: 700, fontSize: 14,
                            color: 'var(--text)',
                            position: 'sticky', left: 0, background: rowBg, zIndex: 5,
                            borderRight: '1px solid var(--border)',
                          }}>
                            <span style={{ marginRight: 8, fontSize: 12, color: 'var(--dim)' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                            {row.nomenclature_name}
                          </td>
                          {cols.map(t => {
                            const texData = cellOf(row, t.id);
                            const remaining = texData?.remaining || 0;
                            const cc = cellColor(remaining);
                            const nw = cellIsNow(row, t.id);
                            return (
                              <td key={t.id}
                                title={nw ? `Сейчас на прессе: ${nw.order}` : undefined}
                                style={{
                                  padding: '12px 16px', textAlign: 'center',
                                  borderLeft: '1px solid var(--border)',
                                  background: nw ? 'var(--accent-weak)'
                                    : cc ? cc.bg : 'transparent',
                                  boxShadow: nw ? 'inset 3px 0 0 var(--accent)' : undefined,
                                }}>
                                {remaining > 0 ? (
                                  <span
                                    className={nw && nw.working ? 'now-pulse' : undefined}
                                    style={{
                                      color: nw ? 'var(--accent)' : cc?.color,
                                      fontWeight: 800, fontSize: 16,
                                    }}>
                                    {remaining}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--dim)', fontSize: 12 }}>—</span>
                                )}
                                {/* Почему цифра мигает — словом, а не догадкой */}
                                {nw && (
                                  <div style={{
                                    color: 'var(--accent)', fontSize: 10, fontWeight: 700,
                                    marginTop: 2, letterSpacing: 0.4,
                                  }}>{nw.working ? 'идёт' : 'загружен'}</div>
                                )}
                              </td>
                            );
                          })}
                          <td style={{
                            padding: '12px 16px', textAlign: 'center',
                            borderLeft: '2px solid var(--border)',
                            color: 'var(--text)', fontWeight: 900, fontSize: 16,
                          }}>
                            {row.total_remaining}
                          </td>
                        </tr>

                        {/* Развёрнутые заказы */}
                        {isExpanded && (
                          <>
                            {/* Собираем все уникальные заказы этого продукта */}
                            {Object.values(byPress ? (row.by_press || {}) : row.by_texture)
                              .flatMap(v => v.orders)
                              .filter((o, i, arr) => arr.findIndex(x => x.order_number === o.order_number) === i)
                              .map(order => (
                                <tr key={order.order_number} style={{
                                  background: rowBg,
                                  borderBottom: '1px solid var(--border)',
                                }}>
                                  <td style={{
                                    padding: '8px 16px 8px 40px', fontSize: 12,
                                    color: 'var(--muted)',
                                    position: 'sticky', left: 0, background: rowBg, zIndex: 5,
                                    borderRight: '1px solid var(--border)',
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{
                                        background: STATUS_COLOR[order.status] + '20',
                                        color: STATUS_COLOR[order.status],
                                        borderRadius: 4, padding: '1px 6px',
                                        fontSize: 10, fontWeight: 700,
                                      }}>
                                        {STATUS_LABEL[order.status] || order.status}
                                      </span>
                                      <span style={{ fontWeight: 600 }}>{order.order_number}</span>
                                      <span style={{ color: 'var(--dim)' }}>— {order.client_name}</span>
                                    </div>
                                  </td>
                                  {cols.map(t => {
                                    const texOrders = cellOf(row, t.id)?.orders || [];
                                    const ord = texOrders.find(o => o.order_number === order.order_number);
                                    const rem = ord?.remaining || 0;
                                    return (
                                      <td key={t.id} style={{
                                        padding: '8px 16px', textAlign: 'center',
                                        borderLeft: '1px solid var(--border)',
                                        color: 'var(--muted)', fontSize: 13,
                                      }}>
                                        {rem > 0 ? (
                                          <span>
                                            <b style={{ color: 'var(--text)' }}>{rem}</b>
                                            <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                                              /{ord?.quantity}
                                            </span>
                                          </span>
                                        ) : ''}
                                      </td>
                                    );
                                  })}
                                  <td style={{ borderLeft: '2px solid var(--border)' }} />
                                </tr>
                              ))}
                            <tr><td colSpan={cols.length + 2} style={{ height: 4, background: 'var(--border)' }} /></tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Итоговая строка */}
                  <tr style={{ background: 'var(--bg)', borderTop: '2px solid var(--border)' }}>
                    <td style={{
                      padding: '12px 16px', fontWeight: 700, fontSize: 13, color: 'var(--muted)',
                      position: 'sticky', left: 0, background: 'var(--bg)',
                      borderRight: '1px solid var(--border)',
                    }}>
                      ИТОГО к выполнению:
                    </td>
                    {cols.map(t => {
                      // Через cellOf, а не напрямую по by_texture: столбцы
                      // переключаются на прессы, и в этом режиме итог по
                      // каждому был пустым — вся строка показывала прочерки,
                      // хотя к прессу подходят именно за этим числом.
                      const total = rows.reduce(
                        (sum, r) => sum + (cellOf(r, t.id)?.remaining || 0), 0);
                      return (
                        <td key={t.id} style={{
                          padding: '12px 16px', textAlign: 'center',
                          borderLeft: '1px solid var(--border)',
                          color: total > 0 ? 'var(--text)' : 'var(--dim)',
                          fontWeight: 800, fontSize: 15,
                        }}>
                          {total > 0 ? total : '—'}
                        </td>
                      );
                    })}
                    <td style={{
                      padding: '12px 16px', textAlign: 'center',
                      borderLeft: '2px solid var(--border)',
                      color: '#ef4444', fontWeight: 900, fontSize: 17,
                    }}>
                      {totalRemain}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 10, flexShrink: 0, display: 'flex', gap: 16,
                          color: 'var(--dim)', fontSize: 12 }}>
              <span>🔴 Много (&gt;200)</span>
              <span>{t('🟡 Средне (100–200)')}</span>
              <span>🟢 Мало (&lt;100)</span>
              <span style={{ marginLeft: 'auto' }}>{t('▶ Нажмите на строку чтобы раскрыть заказы')}</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
